export type ModelPathSegment = string | number;

function parseModelPath(path: string): ModelPathSegment[] {
  return path
    .split(".")
    .filter(Boolean)
    .map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment));
}

function readChild(target: unknown, segment: ModelPathSegment) {
  if (Array.isArray(target) && typeof segment === "number") {
    return target[segment];
  }

  if (!target || typeof target !== "object") {
    throw new Error(
      `Cannot resolve "${String(segment)}" on a non-object value`
    );
  }

  return (target as Record<string, unknown>)[String(segment)];
}

function readParentAtPath(root: unknown, path: string) {
  const segments = parseModelPath(path);

  if (segments.length === 0) {
    throw new Error("Model path cannot be empty");
  }

  let current = root;
  for (const segment of segments.slice(0, -1)) {
    current = readChild(current, segment);
    if (current === undefined || current === null) {
      throw new Error(`Model path "${path}" does not exist`);
    }
  }

  return {
    parent: current,
    lastSegment: segments[segments.length - 1],
  };
}

export function getValueAtModelPath(root: unknown, path: string): unknown {
  const segments = parseModelPath(path);

  if (segments.length === 0) {
    return root;
  }

  return segments.reduce(
    (current, segment) => readChild(current, segment),
    root
  );
}

export function setValueAtModelPath(
  root: unknown,
  path: string,
  nextValue: unknown
) {
  const { parent, lastSegment } = readParentAtPath(root, path);

  if (Array.isArray(parent) && typeof lastSegment === "number") {
    if (lastSegment < 0 || lastSegment >= parent.length) {
      throw new Error(
        `Array index ${lastSegment} is out of bounds for "${path}"`
      );
    }
    parent[lastSegment] = nextValue;
    return;
  }

  if (!parent || typeof parent !== "object") {
    throw new Error(
      `Model path "${path}" does not resolve to a writable parent`
    );
  }

  (parent as Record<string, unknown>)[String(lastSegment)] = nextValue;
}

export function appendItemAtModelPath(
  root: unknown,
  path: string,
  nextValue: unknown
) {
  const value = getValueAtModelPath(root, path);
  if (!Array.isArray(value)) {
    throw new Error(`Model path "${path}" does not resolve to an array`);
  }

  value.push(nextValue);
}

export function removeItemAtModelPath(
  root: unknown,
  path: string,
  index: number
) {
  const value = getValueAtModelPath(root, path);
  if (!Array.isArray(value)) {
    throw new Error(`Model path "${path}" does not resolve to an array`);
  }

  value.splice(index, 1);
}

export function joinModelPath(basePath: string, relativePath?: string) {
  if (!relativePath) {
    return basePath;
  }
  if (!basePath) {
    return relativePath;
  }
  return `${basePath}.${relativePath}`;
}
